// store/useFilterOptionsStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ValueOption } from 'types/common';
import strings from 'content/strings';
import { storeResetFns } from './storeRegistry';

const TTL_MS = 5 * 60 * 1000; // 5 minutes

export type PageTabState = {
  analytics_overview: {
    activeTab: 'app_installs' | 'events';
    activeSubTab?: 'key_events' | 'form';
  };

  analytics_user: {
    activeTab: 'analytics' | 'user_directory';
  };

  analytics_user_details: {
    activeTab: 'appDetails' | 'activity';
  };

  help_and_support: {
    activeTab: 'app_store' | 'play_store';
  };

  project_updates: {
    activeTab: 'analytics' | 'project_updates';
    activeSubTab?: 'progress_reports' | 'progress_videos';
  };
};

export const DEFAULT_PAGE_TABS: PageTabState = {
  analytics_overview: {
    activeTab: 'app_installs',
    activeSubTab: 'key_events',
  },

  analytics_user: {
    activeTab: 'analytics',
  },

  analytics_user_details: {
    activeTab: 'appDetails',
  },

  help_and_support: {
    activeTab: 'app_store',
  },

  project_updates: {
    activeTab: 'analytics',
    activeSubTab: 'progress_reports',
  },
};

const getDefaultPageTabs = (): PageTabState => ({
  analytics_overview: {
    ...DEFAULT_PAGE_TABS.analytics_overview,
  },

  analytics_user: {
    ...DEFAULT_PAGE_TABS.analytics_user,
  },

  analytics_user_details: {
    ...DEFAULT_PAGE_TABS.analytics_user_details,
  },

  help_and_support: {
    ...DEFAULT_PAGE_TABS.help_and_support,
  },

  project_updates: {
    ...DEFAULT_PAGE_TABS.project_updates,
  },
});

interface FilterOptionsStore {
  cityList: ValueOption[] | null;
  cityFetchedAt: number | null;
  isFetchingCities: boolean;
  cityError: string | null;
  tabs: PageTabState;
  setTab: <TPage extends keyof PageTabState>(
    page: TPage,
    data: Partial<PageTabState[TPage]>
  ) => void;
  resetTab: (page: keyof PageTabState) => void;
  resetTabs: () => void;
  fetchCities: (apiFn: () => Promise<ValueOption[]>) => Promise<ValueOption[]>;
  invalidateFilterOptionsStore: () => void;
}

const getFilterValuesInitialState = () => ({
  cityList: null,
  cityFetchedAt: null,
  isFetchingCities: false,
  cityError: null,
  tabs: getDefaultPageTabs(),
});

export const useFilterOptionsStore = create<FilterOptionsStore>()(
  persist(
    (set, get) => {
      storeResetFns.add(() =>
        set({
          cityList: null,
          cityFetchedAt: null,
          isFetchingCities: false,
          cityError: null,
          tabs: getDefaultPageTabs(),
        })
      );
      return {
        ...getFilterValuesInitialState(),

        fetchCities: async (apiFn) => {
          const { cityList, cityFetchedAt, isFetchingCities } = get();
          const isStale = !cityFetchedAt || Date.now() - cityFetchedAt > TTL_MS;

          if (cityList && !isStale) return cityList;
          if (isFetchingCities) return cityList ?? [];

          set({ isFetchingCities: true, cityError: null });
          try {
            const result = await apiFn();
            set({ cityList: result, cityFetchedAt: Date.now() });
            return result;
          } catch (err) {
            const message = typeof err === 'string' ? err : strings.projectUpdates.cityFetchError;
            set({ cityError: message });
            throw message;
          } finally {
            set({ isFetchingCities: false });
          }
        },
        setTab: (page, data) =>
          set((state) => ({
            tabs: {
              ...state.tabs,

              [page]: {
                ...state.tabs[page],
                ...data,
              },
            },
          })),

        resetTab: (page) =>
          set((state) => ({
            tabs: {
              ...state.tabs,

              [page]: { ...DEFAULT_PAGE_TABS[page] },
            },
          })),
        resetTabs: () => set({ tabs: getDefaultPageTabs() }),
        invalidateFilterOptionsStore: () =>
          set({ cityList: null, cityFetchedAt: null, tabs: getDefaultPageTabs() }),
      };
    },
    {
      name: 'filterOptionsStore',
      partialize: (state) => ({
        tabs: state.tabs,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<FilterOptionsStore> | undefined;
        return {
          ...currentState,
          ...persisted,
          tabs: {
            analytics_overview: {
              ...DEFAULT_PAGE_TABS.analytics_overview,
              ...persisted?.tabs?.analytics_overview,
            },
            analytics_user: {
              ...DEFAULT_PAGE_TABS.analytics_user,
              ...persisted?.tabs?.analytics_user,
            },
            analytics_user_details: {
              ...DEFAULT_PAGE_TABS.analytics_user_details,
              ...persisted?.tabs?.analytics_user_details,
            },
            help_and_support: {
              ...DEFAULT_PAGE_TABS.help_and_support,
              ...persisted?.tabs?.help_and_support,
            },
            project_updates: {
              ...DEFAULT_PAGE_TABS.project_updates,
              ...persisted?.tabs?.project_updates,
            },
          },
        };
      },
    }
  )
);
